/**
 * Responsive Tutorial Utilities
 * 
 * Provides utilities for making the tutorial system responsive across different
 * screen sizes and device types. Handles dynamic styling, positioning, and
 * content adaptation based on viewport dimensions.
 * 
 * Requirements: 6.5
 */

import React from 'react';
import { Styles as JoyrideStyles } from 'react-joyride';
import type { TutorialStep } from '../types/tutorial';

/**
 * Breakpoint definitions for responsive design
 * Note: tablet breakpoint is 769 to align with CSS @media (max-width: 768px)
 * This ensures JavaScript device detection matches CSS media query behavior
 */
export const BREAKPOINTS = {
  mobile: 480,
  tablet: 769,
  desktop: 1024,
  large: 1200,
} as const;

/**
 * Device type detection based on screen width
 */
export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'large';

/**
 * Get current device type based on window width
 */
export function getDeviceType(): DeviceType {
  if (typeof window === 'undefined') return 'desktop';
  
  const width = window.innerWidth;
  
  if (width < BREAKPOINTS.mobile) return 'mobile';
  if (width < BREAKPOINTS.tablet) return 'tablet';
  if (width < BREAKPOINTS.desktop) return 'desktop';
  return 'large';
}

/**
 * Check if current device is mobile
 */
export function isMobile(): boolean {
  return getDeviceType() === 'mobile';
}

/**
 * Check if current device is tablet
 */
export function isTablet(): boolean {
  return getDeviceType() === 'tablet';
}

/**
 * Check if current device is desktop or larger
 */
export function isDesktop(): boolean {
  const deviceType = getDeviceType();
  return deviceType === 'desktop' || deviceType === 'large';
}

/**
 * Responsive tutorial styling configuration
 */
export interface ResponsiveTutorialStyles {
  mobile: Partial<JoyrideStyles>;
  tablet: Partial<JoyrideStyles>;
  desktop: Partial<JoyrideStyles>;
  large: Partial<JoyrideStyles>;
}

/**
 * Default responsive styles for tutorial tooltips with enhanced professional design
 */
export const responsiveStyles: ResponsiveTutorialStyles = {
  mobile: {
    options: {
      primaryColor: '#4a90e2',
      width: 240,
      zIndex: 10300,
    },
    tooltip: {
      borderRadius: 8,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06)',
      fontSize: '12px',
      lineHeight: '1.4',
      padding: '12px',
      backgroundColor: '#ffffff',
      border: '1px solid rgba(74, 144, 226, 0.1)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    tooltipTitle: {
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px',
      color: '#1a202c',
      lineHeight: '1.2',
      letterSpacing: '-0.025em',
    },
    tooltipContent: {
      fontSize: '12px',
      lineHeight: '1.4',
      marginBottom: '12px',
      color: '#4a5568',
    },
    tooltipFooter: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
      marginTop: '12px',
    },
    buttonNext: {
      backgroundColor: '#4a90e2',
      border: 'none',
      borderRadius: '6px',
      color: '#ffffff',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '600',
      padding: '8px 16px',
      flex: '1',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 2px 4px rgba(74, 144, 226, 0.2)',
      letterSpacing: '0.025em',
    },
    buttonBack: {
      backgroundColor: 'transparent',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      color: '#4a5568',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '600',
      padding: '8px 16px',
      flex: '1',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    buttonSkip: {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#718096',
      cursor: 'pointer',
      fontSize: '11px',
      fontWeight: '500',
      padding: '4px 8px',
      textAlign: 'center',
      textDecoration: 'none',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      borderRadius: '4px',
      position: 'absolute',
      top: '8px',
      right: '8px',
    },
    spotlight: {
      borderRadius: '8px',
    },
    overlay: {
      backgroundColor: 'rgba(26, 32, 44, 0.3)',
    },
  },

  tablet: {
    options: {
      primaryColor: '#4a90e2',
      width: 320,
      zIndex: 10300,
    },
    tooltip: {
      borderRadius: 10,
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.06)',
      fontSize: '13px',
      lineHeight: '1.5',
      maxWidth: '400px',
      minWidth: '300px',
      padding: '20px',
      backgroundColor: '#ffffff',
      border: '1px solid rgba(74, 144, 226, 0.1)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    tooltipTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '12px',
      color: '#1a202c',
      lineHeight: '1.3',
      letterSpacing: '-0.025em',
    },
    tooltipContent: {
      fontSize: '13px',
      lineHeight: '1.5',
      marginBottom: '16px',
      color: '#4a5568',
    },
    tooltipFooter: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '20px',
      gap: '12px',
    },
    buttonNext: {
      backgroundColor: '#4a90e2',
      border: 'none',
      borderRadius: '8px',
      color: '#ffffff',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '600',
      padding: '10px 20px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 3px 8px rgba(74, 144, 226, 0.3)',
      letterSpacing: '0.025em',
    },
    buttonBack: {
      backgroundColor: 'transparent',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      color: '#4a5568',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '600',
      padding: '10px 20px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    buttonSkip: {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#718096',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '500',
      padding: '6px 12px',
      textDecoration: 'none',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      borderRadius: '4px',
    },
    spotlight: {
      borderRadius: '12px',
    },
    overlay: {
      backgroundColor: 'rgba(26, 32, 44, 0.3)',
    },
  },

  desktop: {
    options: {
      primaryColor: '#4a90e2',
      width: 360,
      zIndex: 10300,
    },
    tooltip: {
      borderRadius: 12,
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12), 0 3px 8px rgba(0, 0, 0, 0.08)',
      fontSize: '14px',
      lineHeight: '1.5',
      maxWidth: '400px',
      minWidth: '320px',
      padding: '24px',
      backgroundColor: '#ffffff',
      border: '1px solid rgba(74, 144, 226, 0.1)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    tooltipTitle: {
      fontSize: '20px',
      fontWeight: '600',
      marginBottom: '16px',
      color: '#1a202c',
      lineHeight: '1.3',
      letterSpacing: '-0.025em',
    },
    tooltipContent: {
      fontSize: '14px',
      lineHeight: '1.5',
      marginBottom: '20px',
      color: '#4a5568',
    },
    tooltipFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '24px',
      gap: '16px',
    },
    buttonNext: {
      backgroundColor: '#4a90e2',
      border: 'none',
      borderRadius: '8px',
      color: '#ffffff',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      padding: '12px 24px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 3px 8px rgba(74, 144, 226, 0.3)',
      letterSpacing: '0.025em',
    },
    buttonBack: {
      backgroundColor: 'transparent',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      color: '#4a5568',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      padding: '12px 24px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    buttonSkip: {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#718096',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      padding: '8px 16px',
      textDecoration: 'none',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      borderRadius: '6px',
    },
    spotlight: {
      borderRadius: '12px',
    },
    overlay: {
      backgroundColor: 'rgba(26, 32, 44, 0.3)',
    },
  },

  large: {
    options: {
      primaryColor: '#4a90e2',
      width: 380,
      zIndex: 10300,
    },
    tooltip: {
      borderRadius: 14,
      boxShadow: '0 10px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
      fontSize: '14px',
      lineHeight: '1.5',
      maxWidth: '420px',
      minWidth: '340px',
      padding: '28px',
      backgroundColor: '#ffffff',
      border: '1px solid rgba(74, 144, 226, 0.1)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    tooltipTitle: {
      fontSize: '22px',
      fontWeight: '600',
      marginBottom: '18px',
      color: '#1a202c',
      lineHeight: '1.3',
      letterSpacing: '-0.025em',
    },
    tooltipContent: {
      fontSize: '14px',
      lineHeight: '1.5',
      marginBottom: '24px',
      color: '#4a5568',
    },
    tooltipFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '28px',
      gap: '18px',
    },
    buttonNext: {
      backgroundColor: '#4a90e2',
      border: 'none',
      borderRadius: '10px',
      color: '#ffffff',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      padding: '14px 28px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0 3px 8px rgba(74, 144, 226, 0.3)',
      letterSpacing: '0.025em',
    },
    buttonBack: {
      backgroundColor: 'transparent',
      border: '1px solid #e2e8f0',
      borderRadius: '10px',
      color: '#4a5568',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      padding: '14px 28px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    buttonSkip: {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#718096',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      padding: '10px 18px',
      textDecoration: 'none',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      borderRadius: '8px',
    },
    spotlight: {
      borderRadius: '14px',
    },
    overlay: {
      backgroundColor: 'rgba(26, 32, 44, 0.3)',
    },
  },
};

/**
 * Get responsive styles for current device type
 */
export function getResponsiveStyles(deviceType?: DeviceType): Partial<JoyrideStyles> {
  const currentDeviceType = deviceType || getDeviceType();
  return responsiveStyles[currentDeviceType];
}

/**
 * Merge responsive styles with custom overrides
 */
export function mergeResponsiveStyles(
  customStyles: Partial<JoyrideStyles> = {},
  deviceType?: DeviceType
): Partial<JoyrideStyles> {
  const responsiveStyles = getResponsiveStyles(deviceType);
  
  return {
    options: {
      ...responsiveStyles.options,
      ...customStyles.options,
    },
    tooltip: {
      ...responsiveStyles.tooltip,
      ...customStyles.tooltip,
    },
    tooltipTitle: {
      ...responsiveStyles.tooltipTitle,
      ...customStyles.tooltipTitle,
    },
    tooltipContent: {
      ...responsiveStyles.tooltipContent,
      ...customStyles.tooltipContent,
    },
    tooltipFooter: {
      ...responsiveStyles.tooltipFooter,
      ...customStyles.tooltipFooter,
    },
    buttonNext: {
      ...responsiveStyles.buttonNext,
      ...customStyles.buttonNext,
    },
    buttonBack: {
      ...responsiveStyles.buttonBack,
      ...customStyles.buttonBack,
    },
    buttonSkip: {
      ...responsiveStyles.buttonSkip,
      ...customStyles.buttonSkip,
    },
    spotlight: {
      ...responsiveStyles.spotlight,
      ...customStyles.spotlight,
    },
    overlay: {
      ...responsiveStyles.overlay,
      ...customStyles.overlay,
    },
  };
}

/**
 * Responsive tutorial step configuration
 */
export interface ResponsiveTutorialStep extends TutorialStep {
  /** Device-specific target overrides */
  responsiveTarget?: {
    mobile?: TutorialStep['target'];
    tablet?: TutorialStep['target'];
    desktop?: TutorialStep['target'];
    large?: TutorialStep['target'];
  };
  /** Device-specific placement overrides */
  responsivePlacement?: {
    mobile?: TutorialStep['placement'];
    tablet?: TutorialStep['placement'];
    desktop?: TutorialStep['placement'];
    large?: TutorialStep['placement'];
  };
  /** Device-specific content overrides */
  responsiveContent?: {
    mobile?: TutorialStep['content'];
    tablet?: TutorialStep['content'];
    desktop?: TutorialStep['content'];
    large?: TutorialStep['content'];
  };
  /** Device-specific style overrides */
  responsiveStyles?: {
    mobile?: TutorialStep['styles'];
    tablet?: TutorialStep['styles'];
    desktop?: TutorialStep['styles'];
    large?: TutorialStep['styles'];
  };
}

/**
 * Adapt tutorial step for current device type
 */
export function adaptStepForDevice(
  step: ResponsiveTutorialStep,
  deviceType?: DeviceType
): TutorialStep {
  const currentDeviceType = deviceType || getDeviceType();
  
  // Get device-specific overrides
  const responsiveTarget = step.responsiveTarget?.[currentDeviceType];
  const responsivePlacement = step.responsivePlacement?.[currentDeviceType];
  const responsiveContent = step.responsiveContent?.[currentDeviceType];
  const responsiveStyles = step.responsiveStyles?.[currentDeviceType];
  
  return {
    ...step,
    target: responsiveTarget || step.target,
    placement: responsivePlacement || step.placement,
    content: responsiveContent || step.content,
    styles: responsiveStyles ? { ...step.styles, ...responsiveStyles } : step.styles,
  };
}

/**
 * Adapt multiple tutorial steps for current device type
 */
export function adaptStepsForDevice(
  steps: ResponsiveTutorialStep[],
  deviceType?: DeviceType
): TutorialStep[] {
  return steps.map(step => adaptStepForDevice(step, deviceType));
}

/**
 * Get responsive tutorial configuration
 */
export function getResponsiveTutorialConfig(deviceType?: DeviceType) {
  const currentDeviceType = deviceType || getDeviceType();
  const isMobile = currentDeviceType === 'mobile';
  
  return {
    continuous: true,
    showProgress: !isMobile,
    showSkipButton: true,
    scrollToFirstStep: false,
    scrollDuration: 0,
    disableOverlay: false,
    disableOverlayClose: false,
    disableScrollParentFix: isMobile, // Disable on mobile to prevent positioning issues
    hideCloseButton: false,
    spotlightClicks: false,
    spotlightPadding: isMobile ? 8 : 12,
    floaterProps: {
      disableAnimation: false,
      hideArrow: false,
      offset: isMobile ? 10 : 15,
      options: {
        preventOverflow: {
          enabled: true,
          boundariesElement: 'viewport' as const,
          padding: isMobile ? 20 : 40,
        },
      },
    },
    styles: getResponsiveStyles(currentDeviceType),
    locale: {
      back: 'Back',
      close: 'Close',
      last: 'Finish',
      next: 'Next',
      skip: 'Skip Tutorial',
    },
  };
}

/**
 * Hook for responsive tutorial functionality
 */
export function useResponsiveTutorial() {
  const [deviceType, setDeviceType] = React.useState<DeviceType>(getDeviceType);
  
  React.useEffect(() => {
    const handleResize = () => {
      setDeviceType(getDeviceType());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const responsiveConfig = React.useMemo(
    () => getResponsiveTutorialConfig(deviceType),
    [deviceType]
  );
  
  const adaptSteps = React.useCallback(
    (steps: ResponsiveTutorialStep[]) => adaptStepsForDevice(steps, deviceType),
    [deviceType]
  );
  
  return {
    deviceType,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop' || deviceType === 'large',
    responsiveConfig,
    adaptSteps,
    mergeResponsiveStyles: (customStyles?: Partial<JoyrideStyles>) =>
      mergeResponsiveStyles(customStyles, deviceType),
  };
}